import routes from 'routes';
import { Provider } from 'store/api/enums';
import { EntityStatus } from 'store/app/enums';
import { shouldShowCaptureAndTransactionsTabs } from 'utils/captureTransactionsAccess';
import { areReceiptsScanPrerequisitesMetForOrganisation } from 'components/OrganisationDetails/receipts-scan-prerequisites';
import { entityHasCaptureEnabled } from 'utils/entity-capture-column';
import { shouldRestrictDisconnectedProviderTabs } from 'utils/get-provider-type';
import type { Organisation, ReceiptEntityStatistics } from 'store/organisation/models';

/** Routes hidden from the installed PWA (desktop browser unchanged). */
export const PWA_HIDDEN_ROUTE_PREFIXES = [
  routes.users,
  routes.vies.qualifiedEntities,
  routes.planner,
  routes.reporting,
] as const;

export type PwaBottomNavItemId =
  | 'home'
  | 'review'
  | 'capture'
  | 'returns'
  | 'more'
  /** @deprecated legacy ids — prefer review / returns */
  | 'entities'
  | 'taxReturns';

/** Phone-width viewport — matches existing PWA CSS (`min-width: 768px` desktop). */
export const PWA_MOBILE_VIEWPORT_MQ = '(max-width: 767px)';

/** Desktop/laptop can use getUserMedia; phones keep the OS camera/gallery sheet. */
export function canUseDesktopWebcam(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  if (isPhoneMobileDevice()) {
    return false;
  }
  return Boolean(navigator.mediaDevices?.getUserMedia);
}

/** True for phone UAs (not tablets). Landscape phones still count. */
export function isPhoneMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  // Tablets stay on desktop layout
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) {
    return false;
  }
  // iPadOS 13+ may report as MacIntel with touch
  if (
    navigator.platform === 'MacIntel' &&
    (navigator.maxTouchPoints || 0) > 1
  ) {
    return false;
  }
  // Android tablets omit "Mobile"
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) {
    return false;
  }
  return /Android.*Mobile|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini|Mobile.*Firefox|Windows Phone/i.test(
    ua
  );
}

/**
 * PWA mobile chrome (bottom nav, phone layouts, bottom sheets).
 * Enabled for installed PWA, phone browsers, narrow viewports, or `?pwaNav=1`.
 * Opt out with `?pwaNav=0`.
 */
export function isPwaMobileNavEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const pwaNav = new URLSearchParams(window.location.search).get('pwaNav');
    if (pwaNav === '0') {
      return false;
    }
    if (pwaNav === '1') {
      return true;
    }
  } catch {
    // fall through
  }

  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    return true;
  }
  if (
    'standalone' in window.navigator &&
    (window.navigator as Navigator & { standalone?: boolean }).standalone
  ) {
    return true;
  }

  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia(PWA_MOBILE_VIEWPORT_MQ).matches
  ) {
    return true;
  }

  return isPhoneMobileDevice();
}

/**
 * PWA: creating new tax returns / submissions is disabled.
 * Users may only view and edit existing ones.
 */
export function isPwaTaxReturnCreationAllowed(): boolean {
  return !isPwaMobileNavEnabled();
}

export function isPwaHiddenRoute(pathname: string): boolean {
  return PWA_HIDDEN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export const PWA_SCAN_CAMERA_PARAM = 'scan';
export const PWA_SCAN_CAMERA_VALUE = 'camera';

export function isPwaScanCameraRequested(search: string): boolean {
  try {
    return (
      new URLSearchParams(search).get(PWA_SCAN_CAMERA_PARAM) ===
      PWA_SCAN_CAMERA_VALUE
    );
  } catch {
    return false;
  }
}

/** Manifest shortcut entry and deep-link target for opening the device camera to scan. */
export function getPwaScanCameraShortcutUrl(): string {
  return `/?${PWA_SCAN_CAMERA_PARAM}=${PWA_SCAN_CAMERA_VALUE}`;
}

export function buildPwaCaptureScanUrl(basePath: string): string {
  const queryIndex = basePath.indexOf('?');
  const pathname = queryIndex >= 0 ? basePath.slice(0, queryIndex) : basePath;
  const params = new URLSearchParams(
    queryIndex >= 0 ? basePath.slice(queryIndex + 1) : ''
  );
  params.set('tab', 'capture');
  params.set(PWA_SCAN_CAMERA_PARAM, PWA_SCAN_CAMERA_VALUE);
  if (isPwaMobileNavEnabled() && !params.has('pwaNav')) {
    params.set('pwaNav', '1');
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function stripPwaScanCameraParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(PWA_SCAN_CAMERA_PARAM);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function getPwaTaxReturnsPath(): string {
  // Full Tax Returns list (Due / Expired / Awaiting Client cards). Do not lock to
  // Submitted-only — that left cardOverrideSource stuck and made filters appear broken.
  if (typeof window !== 'undefined' && isPwaMobileNavEnabled()) {
    try {
      if (new URLSearchParams(window.location.search).get('pwaNav') === '1') {
        return `${routes.submissions}?pwaNav=1`;
      }
    } catch {
      // fall through
    }
  }
  return routes.submissions;
}

/**
 * Previously forced ?statusFilter=Submitted for PWA. That conflicted with the
 * Tax Returns summary cards (open returns). Keep as no-op so deep links work.
 */
export function shouldRedirectPwaSubmissions(
  _pathname: string,
  _search: string
): boolean {
  return false;
}

export function isPwaCaptureEligibleEntity(entity: Organisation): boolean {
  if (!entity?.uuid) {
    return false;
  }
  if (!shouldShowCaptureAndTransactionsTabs(entity.providerName)) {
    return false;
  }
  if (
    entity.status === EntityStatus.DELETED ||
    entity.status === EntityStatus.VIEW_ONLY
  ) {
    return false;
  }

  const provider = entity.providerName?.toLowerCase().trim();

  // PWA Capture opens the device picker for receipt / invoice scanning — not Excel upload.
  if (provider === Provider.EXCEL.toLowerCase()) {
    return false;
  }

  const isReceiptsProvider = provider === Provider.RECEIPTS.toLowerCase();
  const needsReconnect = !isReceiptsProvider && !entity.connectedProperly;

  // Disconnected accounting entities that can capture again after reconnect.
  if (needsReconnect) {
    return true;
  }

  // Connected entities: only those that finished Before you scan.
  if (entity.receiptScanStepsCompleted !== true) {
    return false;
  }

  return true;
}

export type PwaCaptureReadinessOptions = {
  isCoaSkipped?: (organisationId: string) => boolean;
  statsByConnectionId?: Map<string, ReceiptEntityStatistics>;
};

/** Entity eligible for PWA capture and past the Before you scan setup (steps 1–3). */
export function isPwaCaptureReadyEntity(
  entity: Organisation,
  options?: PwaCaptureReadinessOptions
): boolean {
  if (!isPwaCaptureEligibleEntity(entity)) {
    return false;
  }

  if (entityHasCaptureEnabled(entity, options)) {
    return true;
  }

  const provider = entity.providerName?.toLowerCase().trim();
  const isReceiptsProvider = provider === Provider.RECEIPTS.toLowerCase();
  if (!isReceiptsProvider) {
    return false;
  }

  return areReceiptsScanPrerequisitesMetForOrganisation(entity, {
    coaSkipped: options?.isCoaSkipped?.(entity.uuid) ?? false,
  });
}

function sortPwaCaptureEntities(
  entities: Organisation[],
  pinnedEntityIds: string[] = []
): Organisation[] {
  return entities.slice().sort((a, b) => {
    const aPinned = pinnedEntityIds.indexOf(a.uuid);
    const bPinned = pinnedEntityIds.indexOf(b.uuid);
    if (aPinned >= 0 && bPinned >= 0) return aPinned - bPinned;
    if (aPinned >= 0) return -1;
    if (bPinned >= 0) return 1;
    return (a.friendlyName || a.legalName || '').localeCompare(
      b.friendlyName || b.legalName || '',
      undefined,
      { sensitivity: 'base' }
    );
  });
}

export function getPwaCaptureReadyEntities(
  entities: Organisation[] | undefined,
  options?: PwaCaptureReadinessOptions,
  pinnedEntityIds: string[] = []
): Organisation[] {
  if (!entities?.length) {
    return [];
  }

  return sortPwaCaptureEntities(
    entities.filter((entity) => isPwaCaptureReadyEntity(entity, options)),
    pinnedEntityIds
  );
}

export function getPwaCaptureEntities(
  entities: Organisation[] | undefined,
  pinnedEntityIds: string[] = []
): Organisation[] {
  if (!entities?.length) {
    return [];
  }

  return sortPwaCaptureEntities(
    entities.filter(isPwaCaptureEligibleEntity),
    pinnedEntityIds
  );
}

export function getPwaCapturePath(
  entities: Organisation[] | undefined,
  options?: PwaCaptureReadinessOptions,
  pinnedEntityIds: string[] = []
): string | null {
  const captureEntity = getPwaCaptureReadyEntities(
    entities,
    options,
    pinnedEntityIds
  )[0];
  if (!captureEntity?.uuid) {
    return null;
  }
  return routes.organisations.organisation.replace(':id', captureEntity.uuid);
}

export function buildPwaEntityCaptureNavigateUrl(organisationId: string): string {
  const path = routes.organisations.organisation.replace(':id', organisationId);
  const params = new URLSearchParams({ tab: 'capture' });
  if (isPwaMobileNavEnabled()) {
    params.set('pwaNav', '1');
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function buildPwaEntityTaxReturnsNavigateUrl(organisationId: string): string {
  const path = routes.organisations.organisation.replace(':id', organisationId);
  const params = new URLSearchParams({ tab: 'vat' });
  if (isPwaMobileNavEnabled()) {
    params.set('pwaNav', '1');
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function buildPwaEntityTransactionsNavigateUrl(
  organisationId: string
): string {
  const path = routes.organisations.organisation.replace(':id', organisationId);
  const params = new URLSearchParams({ tab: 'transactions' });
  if (isPwaMobileNavEnabled()) {
    params.set('pwaNav', '1');
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

type PwaConnectionEntity = {
  providerName?: string | null;
  connectedProperly?: boolean;
  taxRates?: unknown;
};

/** Tax-rate fetch has not written this entity yet. HMRC is never part of that check. */
export function isPwaConnectionUnchecked(
  entity: PwaConnectionEntity | null | undefined
): boolean {
  if (!entity?.providerName) return false;
  if (entity.providerName.toLowerCase() === Provider.HMRC) return false;
  return entity.taxRates === undefined;
}

/** Checked third-party entity whose provider connection failed. */
export function shouldShowPwaDisconnected(
  entity: PwaConnectionEntity | null | undefined
): boolean {
  return (
    shouldRestrictDisconnectedProviderTabs(entity) &&
    !isPwaConnectionUnchecked(entity)
  );
}

/**
 * While a provider is checked and disconnected, non-VAT tabs go to VAT.
 * The requested tab is kept so it can be restored if the entity connects.
 */
export function resolvePwaTabWhileConnectionSettles(
  requestedTab: string,
  entity: PwaConnectionEntity | null | undefined,
  rememberedTab: string | null
): { tab: string; rememberedTab: string | null } {
  if (shouldShowPwaDisconnected(entity)) {
    if (requestedTab && requestedTab !== 'vat') {
      return { tab: 'vat', rememberedTab: requestedTab };
    }
    return { tab: requestedTab || 'vat', rememberedTab };
  }
  if (
    rememberedTab &&
    rememberedTab !== 'vat' &&
    (!requestedTab || requestedTab === 'vat')
  ) {
    return { tab: rememberedTab, rememberedTab: null };
  }
  return { tab: requestedTab, rememberedTab: null };
}

/** Default entity deep-link when opening from Home / Entities (PWA). Prefer Capture. */
export function buildPwaEntityDetailsNavigateUrl(entity: Organisation): string {
  if (!shouldShowCaptureAndTransactionsTabs(entity.providerName)) {
    return buildPwaEntityTaxReturnsNavigateUrl(entity.uuid);
  }
  if (shouldShowPwaDisconnected(entity)) {
    return buildPwaEntityTaxReturnsNavigateUrl(entity.uuid);
  }
  const provider = entity.providerName?.toLowerCase().trim();
  // Excel has no Capture tab on PWA — open Transactions instead.
  if (provider === Provider.EXCEL.toLowerCase()) {
    return buildPwaEntityTransactionsNavigateUrl(entity.uuid);
  }
  return buildPwaEntityCaptureNavigateUrl(entity.uuid);
}

/** Capture tab + auto-open the mobile OS file picker (Camera, Gallery, Drive, etc.). */
export function buildPwaEntityCaptureScanNavigateUrl(organisationId: string): string {
  return buildPwaCaptureScanUrl(
    routes.organisations.organisation.replace(':id', organisationId)
  );
}

export const PWA_ENTITY_TAB_KEYS = ['capture', 'transactions', 'vat'] as const;

export type PwaEntityTabKey = (typeof PWA_ENTITY_TAB_KEYS)[number];

export function getPwaEntityCapturePath(organisationId: string): string {
  return `${routes.organisations.organisation.replace(':id', organisationId)}?tab=capture`;
}

export function getPwaEntityTaxReturnsPath(organisationId: string): string {
  return `${routes.organisations.organisation.replace(':id', organisationId)}?tab=vat`;
}

export function getPwaEntityTransactionsPath(organisationId: string): string {
  return `${routes.organisations.organisation.replace(':id', organisationId)}?tab=transactions`;
}

export function preparePwaCaptureNavigation(): void {
  try {
    sessionStorage.setItem('organisation-details-active-tab', 'capture');
  } catch {
    // ignore private mode
  }
}

export function preparePwaEntityNavigation(): void {
  preparePwaCaptureNavigation();
}

export function isPwaEntityTabKey(tab: string): tab is PwaEntityTabKey {
  return (PWA_ENTITY_TAB_KEYS as readonly string[]).includes(tab);
}

export function getPwaBottomNavActiveId(
  pathname: string,
  search: string
): PwaBottomNavItemId {
  if (pathname.startsWith(routes.account.root)) {
    return 'more';
  }
  if (
    pathname === routes.accessibility ||
    pathname.startsWith(`${routes.accessibility}/`)
  ) {
    return 'more';
  }

  if (pathname.includes('/entities/organisation/')) {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'transactions') return 'review';
    if (tab === 'vat') return 'returns';
    if (tab === 'capture') return 'capture';
    // Default entity landing is Capture
    return 'capture';
  }

  if (pathname.startsWith(routes.submissions) || pathname.startsWith('/vat')) {
    return 'returns';
  }

  // Former Entities list / home picker
  if (
    pathname === routes.organisations.all ||
    pathname === routes.main ||
    pathname === '/' ||
    pathname === routes.home
  ) {
    return 'home';
  }

  return 'home';
}
